import { InfoCircle } from '#app/components/icons/solid'
import {
	type ComponentType,
	type HTMLAttributes,
	type ReactNode,
	type Ref,
} from 'react'
import {
	type InputProps as AriaInputProps,
	type TextFieldProps as AriaTextFieldProps,
	Input as AriaInput,
	TextField as AriaTextField,
	Group,
} from 'react-aria-components'
import { cx, sortCx } from '#app/utils/cx'
import { HintText } from '../../shared/input/hint-text'
import { Label } from '../../shared/input/label'

interface InputBaseProps extends TextFieldProps {
	size?: 'sm' | 'md'
	placeholder?: string
	iconClassName?: string
	inputClassName?: string
	wrapperClassName?: string
	shortcut?: string | boolean
	ref?: Ref<HTMLInputElement>
	groupRef?: Ref<HTMLDivElement>
	icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>>
}

export const InputBase = ({
	size = 'sm',
	placeholder,
	icon: Icon,
	isRequired,
	isDisabled,
	isInvalid,
	shortcut,
	ref,
	groupRef,
	wrapperClassName,
	inputClassName,
	iconClassName,
	...inputProps
}: InputBaseProps) => {
	// Check if the input has a leading icon or tooltip
	const hasTrailingIcon = isInvalid
	const hasLeadingIcon = Icon

	const sizes = sortCx({
		sm: {
			root: cx(
				'px-3 py-2',
				hasTrailingIcon && 'pr-9',
				hasLeadingIcon && 'pl-10',
			),
			iconLeading: 'left-3',
			iconTrailing: 'right-3',
			shortcut: 'pr-2.5',
		},
		md: {
			root: cx(
				'px-3.5 py-2.5',
				hasTrailingIcon && 'pr-9.5',
				hasLeadingIcon && 'pl-10.5',
			),
			iconLeading: 'left-3.5',
			iconTrailing: 'right-3.5',
			shortcut: 'pr-3',
		},
	})

	return (
		<Group
			{...{ isDisabled, isInvalid }}
			ref={groupRef}
			className={({ isFocusWithin, isDisabled, isInvalid }) =>
				cx(
					'bg-primary ring-border-primary relative flex w-full flex-row place-content-center place-items-center rounded-lg shadow-xs ring-1 transition-shadow duration-100 ease-linear ring-inset',

					isFocusWithin && !isDisabled && 'ring-border-brand ring-2',

					// Disabled state styles
					isDisabled &&
						'bg-disabled_subtle ring-border-disabled cursor-not-allowed',
					'group-disabled:bg-disabled_subtle group-disabled:ring-border-disabled group-disabled:cursor-not-allowed',

					// Invalid state styles
					isInvalid && 'ring-border-error_subtle',
					'group-invalid:ring-border-error_subtle',

					// Invalid state with focus-within styles
					isInvalid && isFocusWithin && 'ring-border-error ring-2',
					isFocusWithin &&
						'group-invalid:ring-border-error group-invalid:ring-2',

					wrapperClassName,
				)
			}
		>
			{/* Leading icon and Payment icon */}
			{Icon && (
				<Icon
					className={cx(
						'text-fg-quaternary pointer-events-none absolute size-5',
						isDisabled && 'text-fg-disabled',
						sizes[size].iconLeading,
						iconClassName,
					)}
				/>
			)}

			{/* Input field */}
			<AriaInput
				{...(inputProps as AriaInputProps)}
				ref={ref}
				placeholder={placeholder}
				className={cx(
					'text-md text-primary placeholder:text-placeholder autofill:text-primary m-0 w-full bg-transparent ring-0 outline-hidden autofill:rounded-lg',
					isDisabled && 'text-disabled cursor-not-allowed',
					sizes[size].root,
					inputClassName,
				)}
			/>

			{/* Invalid icon */}
			{isInvalid && (
				<InfoCircle
					className={cx(
						'text-fg-error-secondary pointer-events-none absolute size-4',
						sizes[size].iconTrailing,
					)}
				/>
			)}

			{/* Shortcut */}
			{shortcut && (
				<div
					className={cx(
						'to-bg-primary pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-40% pl-8',
						sizes[size].shortcut,
					)}
				>
					<span
						className={cx(
							'text-quaternary ring-border-secondary pointer-events-none rounded px-1 py-px text-xs font-medium ring-1 select-none ring-inset',
							isDisabled && 'text-disabled bg-transparent',
						)}
						aria-hidden="true"
					>
						{typeof shortcut === 'string' ? shortcut : '⌘K'}
					</span>
				</div>
			)}
		</Group>
	)
}

InputBase.displayName = 'InputBase'

interface TextFieldProps extends AriaTextFieldProps {
	ref?: Ref<HTMLDivElement>
	label?: string
	hint?: ReactNode
}

const TextField = ({ className, ...props }: TextFieldProps) => {
	return (
		<AriaTextField
			{...props}
			className={(state) =>
				cx(
					'group flex h-max w-full flex-col items-start justify-start gap-1.5',
					typeof className === 'function' ? className(state) : className,
				)
			}
		>
			{props.children}
		</AriaTextField>
	)
}

TextField.displayName = 'TextField'

interface InputProps extends InputBaseProps {
	hideRequiredIndicator?: boolean
	label?: string
	hint?: ReactNode
}

export const Input = ({
	size = 'sm',
	placeholder,
	icon: Icon,
	label,
	hint,
	shortcut,
	hideRequiredIndicator,
	className,
	ref,
	groupRef,
	iconClassName,
	inputClassName,
	wrapperClassName,
	...props
}: InputProps) => {
	return (
		<TextField
			aria-label={!label ? placeholder : undefined}
			{...props}
			className={className}
		>
			{label && (
				<Label
					isRequired={
						hideRequiredIndicator ? !hideRequiredIndicator : undefined
					}
				>
					{label}
				</Label>
			)}

			<InputBase
				{...{
					ref,
					groupRef,
					size,
					placeholder,
					icon: Icon,
					shortcut,
					iconClassName,
					inputClassName,
					wrapperClassName,
				}}
			/>

			{hint && <HintText>{hint}</HintText>}
		</TextField>
	)
}

Input.displayName = 'Input'
